import {useParams} from "react-router-dom";
import {useEffect, useState, useRef} from "react";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";

import NavBreadcrumb from "components/NavBreadcrum";
import {useNavigate} from "react-router-dom";
import navs from "../navs";
import axiApi from "ApiCaller";
import TablesContainerMeasure from "./TablesContainerMeasure";
import PaginationBar from "components/PaginationBar";
import MapAssetContainer from "site-apps/assets/MapAssetContainer";
import ParamsAsset from "./ParamsAsset";
import DateUtils from "core/utils/DateUtils";
import {SwalAlertChoice, SwalAlertClass} from "core/swalClass";

import ConditionalRender from "components/ConditionalRender";

const NavInfoModel = {position: null, total: null, next: null, back: null};

const ViewContainerMeasure = () => {
    const items = useRef({
        all: [],
        showed: [],
        chosen: [],
        reset: function () {
            this.chosen = [];
        },
    });

    const {uidp} = useParams();
    const [uid, setUid] = useState(uidp);
    const [error, setError] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [paginatedItems, setPaginatedItems] = useState([]);
    const dateRange = useRef({
        startDate: new Date(),
        endDate: new Date(),
    });
    const [measures, setMeasures] = useState({});
    const [item, setItem] = useState(null);
    const [groupDisp, setGroupDisp] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [activeTab, setActiveTab] = useState(null);

    const {t} = useTranslation("routes");
    const [navInfo, setNavInfo] = useState({...NavInfoModel});
    const navigate = useNavigate();

    const {t: _Tt} = useTranslation("tags");

    const bread = [
        {name: t("labels.history"), url: "/historic/", current: false},
        {name: t("labels.measures"), url: "/historic/measures", current: false},
        {name: uid, current: true},
    ];

    useEffect(() => {
        dateRange.current.endDate = new Date();
        dateRange.current.startDate = new Date();
        dateRange.current.startDate.setDate(dateRange.current.endDate.getDate() - 7);
        setError(null);
        fetchContainerData(uidp);

        return () => {
            sessionStorage.removeItem("viewsHistoricFilters");
        };
    }, []);

    useEffect(() => {
        if (selectedDevice && measures[selectedDevice]) {
            const measureData = measures[selectedDevice];
            if (measureData.measure.length) {
                // Backend ahora devuelve medidas ordenadas por fecha descendente.
                // Copiamos el array para evitar mutaciones y mantener la paginación en frontend.
                const measuresArr = measureData.measure || [];
                items.current.all = measuresArr;
                items.current.showed = measuresArr.slice(0, itemsPerPage);
                setPaginatedItems(items.current.showed);
            } else {
                items.current.all = [];
                items.current.showed = [];
                setPaginatedItems([]);
            }
        }
    }, [selectedDevice]);

    const fetchContainerData = async (uidCall) => {
        try {
            const savedFilters = sessionStorage.getItem("viewsHistoricFilters");

            const filters = {
                start: DateUtils.dateToStringInput(dateRange.current.startDate),
                end: DateUtils.dateToStringInput(dateRange.current.endDate),
                ...(savedFilters ? JSON.parse(savedFilters) : {}),
            };
            const resp = await axiApi.post(`/api/assets/asset/data/${uidCall}/`, filters);
            const data = resp.data || {};
            if (!data || !data.status) {
                setError(data && data.message ? data.message : `Error`);
                items.current.all = [];
                items.current.showed = [];
                setPaginatedItems([]);
                return;
            }
            setMeasures(data.measures);
            setItem(data.item);
            setGroupDisp(data.item.uuids);
            setSelectedDevice(data.item.uuids[0]);
            setActiveTab(data.item.uuids[0]);
            const measureData = data.measures[data.item.uuids[0]];
            if (measureData && measureData.measure) {
                // Backend ahora devuelve medidas ordenadas por fecha descendente.
                const measuresArr = Array.isArray(measureData.measure) ? [...measureData.measure] : [];
                items.current.all = measuresArr;
                items.current.showed = measuresArr.slice(0, itemsPerPage);
                setPaginatedItems(items.current.showed);
            } else {
                items.current.all = [];
                items.current.showed = [];
                setPaginatedItems([]);
            }
            setNavInfo({
                ...NavInfoModel,
                back: data.item.back,
                next: data.item.next,
                position: data.navigate.position,
                total: data.navigate.total,
            });
        } catch (error) {
            setError(String(error));
            items.current.all = [];
            items.current.showed = [];
            setPaginatedItems([]);
        }
    };

    const handleSelectAll = () => {
        if (selectedItems.length === paginatedItems.length) {
            setSelectedItems([]);
            items.current.chosen = [];
        } else {
            const newSelectedItems = [...new Set([...selectedItems, ...paginatedItems])];
            setSelectedItems(newSelectedItems);
            items.current.chosen = newSelectedItems;
        }
    };

    const handleSelectItem = (item) => {
        const isSelected = selectedItems.includes(item);
        const newSelectedItems = isSelected ? selectedItems.filter((i) => i !== item) : [...selectedItems, item];
        setSelectedItems(newSelectedItems);
        items.current.chosen = newSelectedItems;
    };

    const handleDelete = () => {
        const alert = new SwalAlertChoice();
        alert.params.confirmButtonText = "Borrar";
        alert.params.cancelButtonText = "Cancelar";
        alert.warning("Atención", `Se borrarán los ${selectedItems.length} items seleccionados`).then((result) => {
            if (result.isConfirmed) {
                deleteItems();
            }
        });
    };

    const deleteItems = async () => {
        try {
            const deleteRequests = selectedItems.map(async (item) => {
                const [date, value] = item;
                const dateInNanoseconds = date * 10 ** 9;
                const resp = await axiApi.post("/api/assets/asset/data/delete/", {
                    measurement: "measure",
                    date: dateInNanoseconds,
                    uuid: selectedDevice,
                });
                if (!resp.data || !resp.data.status) {
                    throw new Error((resp.data && resp.data.message) || `Error`);
                }
                return true;
            });
            await Promise.all(deleteRequests);
            const remainingItems = items.current.all.filter((item) => !selectedItems.includes(item));
            items.current.all = remainingItems;
            items.current.showed = remainingItems.slice(0, itemsPerPage);
            setPaginatedItems(items.current.showed);
            setSelectedItems([]);
            items.current.chosen = [];
            new SwalAlertClass({confirmButtonText: "Aceptar"}).success("Elementos borrados");
        } catch (error) {
            new SwalAlertClass({confirmButtonText: "Aceptar"}).error(
                "Error",
                String(error) || "Inténtelo de nuevo más tarde o consulte con su administrador",
            );
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        items.current.showed = items.current.all.slice(startIndex, endIndex);
        setPaginatedItems(items.current.showed);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        items.current.showed = items.current.all.slice(0, newItemsPerPage);
        setPaginatedItems(items.current.showed);
    };
    const changeDateRange = (dateRage_) => {
        dateRange.current = dateRage_;
        fetchContainerData(uid);
    };

    return (
        <div>
            <NavBreadcrumb bread={bread} navs={navs} />
            <ConditionalRender condition={!error} fallback={<div>{error}</div>}>
                <>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12 preview-canvas offcanvas-header mt-1">
                                <div className="preview-icon">
                                    <FontAwesomeIcon icon="chart-line" />
                                </div>
                                <div className="d-flex flex-row justify-content-between align-items-center w-100">
                                    <div className="preview-title ps-4">
                                        <label className="d-inline-block small me-2 text-secondary">UID:</label>
                                        <span className="d-inline-block text-black h5">{uid}</span>
                                        <ConditionalRender condition={navInfo.position}>
                                            <small className="text-muted ms-2">
                                                {navInfo.position}/{navInfo.total}
                                            </small>
                                        </ConditionalRender>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <ConditionalRender condition={navInfo.back}>
                                            <button
                                                className="btn btn-outline-secondary px-3 py-1"
                                                title={navInfo.back}
                                                onClick={() => {
                                                    navigate(`/historic/measures/${navInfo.back}/`);
                                                    setUid(navInfo.back);
                                                    fetchContainerData(navInfo.back);
                                                }}>
                                                <FontAwesomeIcon icon="chevron-left" />
                                            </button>
                                        </ConditionalRender>
                                        <ConditionalRender condition={navInfo.next}>
                                            <button
                                                className="btn btn-outline-secondary px-3 py-1"
                                                title={navInfo.next}
                                                onClick={() => {
                                                    navigate(`/historic/measures/${navInfo.next}/`);
                                                    setUid(navInfo.next);
                                                    fetchContainerData(navInfo.next);
                                                }}>
                                                <FontAwesomeIcon icon="chevron-right" />
                                            </button>
                                        </ConditionalRender>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-8 col-12">
                                <div className="row mb-3"></div>
                                {item && (
                                    <ParamsAsset
                                        item={item}
                                        handleDelete={handleDelete}
                                        dateRange={dateRange}
                                        setDateRange={changeDateRange}
                                    />
                                )}
                            </div>
                            <div className="col-md-4 col-12">
                                {item && item.lat && item.lng ? (
                                    <MapAssetContainer openMap={true} coords={[item.lat, item.lng]} />
                                ) : (
                                    <div>{t("no_location_data")}</div>
                                )}
                            </div>
                        </div>
                    </div>
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => {
                            setActiveTab(k);
                            setSelectedDevice(k);
                        }}
                        className="mb-3">
                        {groupDisp.map((uuid, idx) => (
                            <Tab eventKey={uuid} title={uuid} key={idx}>
                                <TablesContainerMeasure
                                    paginatedItems={paginatedItems}
                                    selectedItems={selectedItems}
                                    handleSelectAll={handleSelectAll}
                                    handleSelectItem={handleSelectItem}
                                    t={_Tt}
                                    measures={measures}
                                    device={uuid}
                                />
                            </Tab>
                        ))}
                    </Tabs>
                    <div className="d-flex justify-content-center mt-3">
                        <PaginationBar
                            items={items.current.all}
                            setPageItems={setPaginatedItems}
                            uncheckAll={() => setSelectedItems([])}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                            itemsPerPage={itemsPerPage}
                            onItemsPerPageChange={handleItemsPerPageChange}
                        />
                    </div>
                </>
            </ConditionalRender>
        </div>
    );
};

export default ViewContainerMeasure;
